import React, { useState, useEffect, useContext, useCallback } from "react";
import { Alert, Badge, Button, Card, Col, Row, Spinner, Dropdown, Modal, Form } from "react-bootstrap";
import api from "../api";
import { AuthContext } from "../AuthContext";
import AutoGrowTextarea from "../components/AutoGrowTextarea";

const METRIC_PERIODS = [
  { key: "month", label: "This Month" },
  { key: "quarter", label: "This Quarter" },
  { key: "year", label: "This Year" },
];

const RIDE_STATUS_VARIANT = {
  ASSIGNED: "warning",
  CONFIRMED: "success",
};

// Tappable address: opens the place in Google Maps (the app on phones that
// have it, the browser otherwise). Google Maps' own Directions button takes the
// driver from there to turn-by-turn.
function MapLink({ address }) {
  if (!address) return null;
  const href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {address}
    </a>
  );
}

function formatDateTime(iso) {
  if (!iso) return "—";
  // Ride pickup times are stored as local (Eastern) wall-clock, so parse-as-local
  // is correct here — do NOT mark as UTC.
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

// ---- Add-to-calendar (.ics) -------------------------------------------------
// Generates an iCalendar file the driver can open on a phone (iOS/Android add
// it to the device calendar) or PC (opens in Outlook). ride.pickup_datetime is
// actually the APPOINTMENT time; the driver sets their own pickup time (when
// they'll leave to get the client), which becomes the event start. Times are
// Eastern wall-clock, so we emit a "floating" local time (no TZID/Z): the event
// lands at that clock time in the driver's own calendar.
const pad2 = (n) => String(n).padStart(2, "0");

// Format a Date for an <input type="datetime-local"> value (YYYY-MM-DDTHH:MM).
function toLocalInputValue(d) {
  return (
    d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate()) +
    "T" + pad2(d.getHours()) + ":" + pad2(d.getMinutes())
  );
}

function icsLocal(d) {
  return (
    d.getFullYear() + pad2(d.getMonth() + 1) + pad2(d.getDate()) +
    "T" + pad2(d.getHours()) + pad2(d.getMinutes()) + pad2(d.getSeconds())
  );
}

// Build the shared event from the ride + the driver's chosen pickup time.
// pickupLocalStr is a datetime-local value (YYYY-MM-DDTHH:MM).
function buildRideEvent(ride, pickupLocalStr) {
  const start = new Date(pickupLocalStr);
  if (isNaN(start.getTime())) return null;
  const appt = new Date(ride.pickup_datetime);
  // Event runs from the driver's pickup time until the appointment; if the
  // appointment isn't after pickup, fall back to a 1-hour block.
  const end = !isNaN(appt.getTime()) && appt > start
    ? appt
    : new Date(start.getTime() + 60 * 60 * 1000);

  const summary = `HHH Ride: ${ride.client_name || "Client"}`;
  const location = ride.pickup_location || ride.client_address || "";

  const desc = [];
  if (ride.client_name) desc.push(`Client: ${ride.client_name}`);
  if (ride.client_phone) desc.push(`Client phone: ${ride.client_phone}`);
  if (!isNaN(appt.getTime())) desc.push(`Appointment time: ${formatDateTime(ride.pickup_datetime)}`);
  if (ride.pickup_location) desc.push(`Pickup: ${ride.pickup_location}`);
  if (ride.dropoff_location) desc.push(`Dropoff: ${ride.dropoff_location}`);
  if (ride.provider_name) {
    desc.push(`Appointment: ${ride.provider_name}${ride.provider_address ? " — " + ride.provider_address : ""}`);
  }
  if (ride.client_emergency_contact_name) {
    desc.push(`Emergency contact: ${ride.client_emergency_contact_name}${ride.client_emergency_contact_phone ? " (" + ride.client_emergency_contact_phone + ")" : ""}`);
  }
  if (ride.driver_notes) desc.push(`Driver note: ${ride.driver_notes}`);
  if (ride.notes) desc.push(`Notes: ${ride.notes}`);

  return { start, end, summary, location, description: desc.join("\n") };
}

// Google Calendar template URL — opens Google Calendar (app/site) pre-filled,
// the smoothest path on Android. ctz pins the floating times to Eastern.
function googleCalUrl(ev) {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: ev.summary,
    dates: `${icsLocal(ev.start)}/${icsLocal(ev.end)}`,
    details: ev.description,
    location: ev.location,
    ctz: "America/New_York",
  });
  return "https://calendar.google.com/calendar/render?" + params.toString();
}

export default function DriverDashboardPage() {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null); // assignment_id or ride_id currently acting
  const [actionError, setActionError] = useState("");
  const [calRide, setCalRide] = useState(null); // ride whose pickup-time dialog is open
  const [calPickup, setCalPickup] = useState(""); // chosen pickup time (datetime-local)
  const [noteRide, setNoteRide] = useState(null); // ride whose driver-note dialog is open
  const [noteText, setNoteText] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState("");

  const openNote = (ride) => {
    setNoteRide(ride);
    setNoteText(ride.driver_notes || "");
    setNoteError("");
  };

  // The note belongs to the client, so update it on every card for them.
  const saveNote = async () => {
    if (!noteRide) return;
    setNoteSaving(true);
    setNoteError("");
    try {
      const res = await api.put(`/drivers/clients/${noteRide.client_id}/driver-note`, {
        driver_notes: noteText,
      });
      const saved = res.data.driver_notes || null;
      setData((prev) => ({
        ...prev,
        upcoming_rides: prev.upcoming_rides.map((r) =>
          r.client_id === noteRide.client_id ? { ...r, driver_notes: saved } : r
        ),
      }));
      setNoteRide(null);
    } catch (e) {
      setNoteError(e?.response?.data?.error || "Failed to save the driver note.");
    } finally {
      setNoteSaving(false);
    }
  };

  const openCalendar = (ride) => {
    // Default the pickup time to 30 min before the appointment as a nudge; the
    // driver adjusts it for actual travel time.
    const appt = new Date(ride.pickup_datetime);
    const def = isNaN(appt.getTime()) ? new Date() : new Date(appt.getTime() - 30 * 60 * 1000);
    setCalPickup(toLocalInputValue(def));
    setCalRide(ride);
  };

  const handleAppleOutlook = async () => {
    if (!calRide || !calPickup) return;
    const ride = calRide;
    setCalRide(null);
    try {
      // The .ics is served by the backend (reliable on iOS); a short-lived
      // token authorizes the plain navigation that follows.
      const res = await api.post("/drivers/calendar-token", {
        ride_id: ride.ride_id,
        pickup: calPickup,
      });
      const base = api.defaults.baseURL || "";
      window.location.href = `${base}/drivers/calendar.ics?jwt=${encodeURIComponent(res.data.token)}`;
    } catch {
      setActionError("Could not create the calendar file. Please try again.");
    }
  };

  const handleGoogle = () => {
    if (!calRide || !calPickup) return;
    const ev = buildRideEvent(calRide, calPickup);
    if (ev) window.open(googleCalUrl(ev), "_blank", "noopener");
    setCalRide(null);
  };

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    api
      .get("/dashboard/driver")
      .then((res) => setData(res.data))
      .catch(() => setError("Failed to load dashboard data."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleConfirm = async (assignmentId) => {
    setBusyId(assignmentId);
    setActionError("");
    try {
      await api.patch(`/drivers/assignments/${assignmentId}`, { status: "CONFIRMED" });
      await load();
    } catch (e) {
      setActionError(e?.response?.data?.error || "Failed to confirm ride.");
    } finally {
      setBusyId(null);
    }
  };

  const handleOutcome = async (rideId, status) => {
    const msg =
      status === "COMPLETED"
        ? "Mark this ride as completed?"
        : "Mark this ride as a no-show? (Client didn't answer)";
    if (!window.confirm(msg)) return;
    setBusyId(rideId);
    setActionError("");
    try {
      await api.post(`/drivers/rides/${rideId}/outcome`, { status });
      await load();
    } catch (e) {
      setActionError(e?.response?.data?.error || "Failed to update ride.");
    } finally {
      setBusyId(null);
    }
  };

  if (loading)
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );

  if (error) return <Alert variant="danger">{error}</Alert>;

  const { driver, metrics, upcoming_rides } = data;

  return (
    <div>
      <h4 className="mb-1">Welcome, {user?.full_name}</h4>
      {driver && (
        <p className="text-muted mb-4" style={{ fontSize: "0.9rem" }}>
          Driver profile: <strong>{driver.display_name}</strong>
        </p>
      )}

      {!driver && (
        <Alert variant="warning">
          No driver profile is linked to your account. Contact an administrator.
        </Alert>
      )}

      {/* Ride metrics */}
      <Row className="g-3 mb-4">
        {METRIC_PERIODS.map(({ key, label }) => (
          <Col key={key} xs={12} sm={4}>
            <Card className="text-center h-100 shadow-sm">
              <Card.Header className="fw-semibold bg-light">{label}</Card.Header>
              <Card.Body className="py-3">
                <div style={{ fontSize: "2.5rem", fontWeight: 700, lineHeight: 1 }}>
                  {metrics[key] ?? 0}
                </div>
                <div className="text-muted mt-1" style={{ fontSize: "0.85rem" }}>
                  Rides Completed
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <h5 className="mb-3 d-flex align-items-center gap-2">
        Today &amp; Upcoming Rides
        {upcoming_rides?.length > 0 && <Badge bg="primary">{upcoming_rides.length}</Badge>}
      </h5>

      {actionError && <Alert variant="danger">{actionError}</Alert>}

      {!upcoming_rides || upcoming_rides.length === 0 ? (
        <p className="text-muted">No rides scheduled.</p>
      ) : (
        <div className="d-flex flex-column gap-3">
          {upcoming_rides.map((ride) => {
            const busy = busyId === ride.ride_id || busyId === ride.assignment_id;
            return (
              <Card key={ride.ride_id} className="shadow-sm">
                <Card.Body>
                  <Row className="g-3 align-items-start">
                    {/* When + status */}
                    <Col xs={12} md={3} lg={2}>
                      <div className="fw-semibold">{formatDateTime(ride.pickup_datetime)}</div>
                      <Badge
                        className="mt-1"
                        bg={RIDE_STATUS_VARIANT[ride.assignment_status] || "secondary"}
                        text={ride.assignment_status === "ASSIGNED" ? "dark" : undefined}
                      >
                        {ride.assignment_status === "ASSIGNED" ? "Needs Confirmation" : "Confirmed"}
                      </Badge>
                    </Col>

                    {/* Client + contact info */}
                    <Col xs={12} md={4} lg={4}>
                      <div className="fw-semibold">{ride.client_name}</div>
                      {ride.client_phone && (
                        <div>
                          <i className="bi bi-telephone me-1" />
                          <a href={`tel:${ride.client_phone}`}>{ride.client_phone}</a>
                        </div>
                      )}
                      {ride.client_address && (
                        <div className="text-muted small">
                          <i className="bi bi-geo-alt me-1" />
                          <MapLink address={ride.client_address} />
                        </div>
                      )}
                      {ride.functional_limitations && (
                        <div className="small mt-1">
                          <i className="bi bi-person-wheelchair me-1" />
                          <span className="text-muted">Functional limitations: </span>
                          {ride.functional_limitations}
                        </div>
                      )}
                      {/* Standing note for drivers, shared across all this
                          client's rides. */}
                      {ride.driver_notes ? (
                        <div className="small mt-2 p-2 rounded border border-warning bg-warning-subtle">
                          <div className="d-flex justify-content-between align-items-start gap-2">
                            <span className="fw-semibold">
                              <i className="bi bi-sticky me-1" />Driver note
                            </span>
                            <Button
                              size="sm"
                              variant="link"
                              className="p-0"
                              onClick={() => openNote(ride)}
                            >
                              Edit
                            </Button>
                          </div>
                          <div style={{ whiteSpace: "pre-wrap" }}>{ride.driver_notes}</div>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="link"
                          className="p-0 mt-1"
                          onClick={() => openNote(ride)}
                        >
                          <i className="bi bi-plus-circle me-1" />Add driver note
                        </Button>
                      )}
                      {ride.client_emergency_contact_name && (
                        <div className="small mt-1">
                          <i className="bi bi-exclamation-circle me-1" />
                          <span className="text-muted">Emergency contact: </span>
                          {ride.client_emergency_contact_name}
                          {ride.client_emergency_contact_phone && (
                            <> (<a href={`tel:${ride.client_emergency_contact_phone}`}>{ride.client_emergency_contact_phone}</a>)</>
                          )}
                        </div>
                      )}
                    </Col>

                    {/* Appointment / trip info */}
                    <Col xs={12} md={5} lg={4} className="small">
                      {ride.provider_name && (
                        <div className="mb-1">
                          <span className="text-muted">Appointment: </span>
                          {ride.provider_name}
                          {ride.provider_address ? (
                            <> — <MapLink address={ride.provider_address} /></>
                          ) : null}
                          {ride.provider_phone ? (
                            <>
                              {" "}
                              (<a href={`tel:${ride.provider_phone}`}>{ride.provider_phone}</a>)
                            </>
                          ) : null}
                        </div>
                      )}
                      <div>
                        <span className="text-muted">Pickup: </span>
                        {ride.pickup_location ? <MapLink address={ride.pickup_location} /> : "—"}
                      </div>
                      <div>
                        <span className="text-muted">Dropoff: </span>
                        {ride.dropoff_location ? <MapLink address={ride.dropoff_location} /> : "—"}
                      </div>
                      {ride.notes && (
                        <div><span className="text-muted">Notes: </span>{ride.notes}</div>
                      )}
                    </Col>

                    {/* Actions */}
                    <Col xs={12} lg={2} className="d-flex flex-wrap gap-2 justify-content-lg-end">
                      {ride.assignment_status === "ASSIGNED" && (
                        <Button
                          size="sm"
                          variant="success"
                          disabled={busy}
                          onClick={() => handleConfirm(ride.assignment_id)}
                        >
                          {busy ? <Spinner size="sm" animation="border" /> : "Confirm"}
                        </Button>
                      )}
                      <Dropdown align="end">
                        <Dropdown.Toggle
                          size="sm"
                          variant="outline-primary"
                          disabled={busy}
                          style={{ minWidth: "8rem" }}
                        >
                          {busy ? "Working…" : "Status"}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => handleOutcome(ride.ride_id, "COMPLETED")}>
                            Completed
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleOutcome(ride.ride_id, "NO_SHOW")}>
                            No Show
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => openCalendar(ride)}
                        title="Add this ride to your phone or Outlook calendar"
                      >
                        <i className="bi bi-calendar-plus me-1" />Calendar
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}

      <Modal show={!!calRide} onHide={() => setCalRide(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Ride to Calendar</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {calRide && (
            <>
              <p className="mb-3">
                <span className="text-muted">Appointment: </span>
                <strong>{formatDateTime(calRide.pickup_datetime)}</strong>
                {calRide.client_name ? ` — ${calRide.client_name}` : ""}
              </p>
              <Form.Group>
                <Form.Label>Your pickup time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={calPickup}
                  onChange={(e) => setCalPickup(e.target.value)}
                  autoFocus
                />
                <Form.Text className="text-muted">
                  When you’ll leave to pick up the client. The calendar event runs
                  from this time until the appointment.
                </Form.Text>
              </Form.Group>
              <p className="text-muted small mt-3 mb-0">
                Choose where to add it. Use <strong>Google Calendar</strong> on most
                Android phones, or <strong>Apple / Outlook</strong> on iPhone and PC.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="secondary" onClick={() => setCalRide(null)}>Cancel</Button>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={handleGoogle} disabled={!calPickup}>
              <i className="bi bi-google me-1" />Google Calendar
            </Button>
            <Button variant="primary" onClick={handleAppleOutlook} disabled={!calPickup}>
              <i className="bi bi-calendar-plus me-1" />Apple / Outlook
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Driver note dialog */}
      <Modal show={!!noteRide} onHide={() => !noteSaving && setNoteRide(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Driver Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {noteRide && (
            <p className="small text-muted">
              For <strong>{noteRide.client_name}</strong>. Shown to every driver on
              every ride for this client.
            </p>
          )}
          {noteError && <Alert variant="danger">{noteError}</Alert>}
          <AutoGrowTextarea
            rows={3}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="e.g., Dog in yard, use side door. Needs help with steps."
            maxLength={2000}
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setNoteRide(null)} disabled={noteSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveNote} disabled={noteSaving}>
            {noteSaving ? <Spinner size="sm" animation="border" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
